import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiSparkles, HiClipboardCopy, HiCheck } from 'react-icons/hi';
import { aiAPI } from '../services/api';
import { notify } from '../utils/notify';
import { mensajeDeError } from '../utils/apiError';
import { dinero } from '../utils/economia';

/**
 * Redactar el anuncio, proponer precio y dar ideas para promocionarlo.
 *
 * ## Por qué esto existe
 *
 * El plan Pro vendía tres cosas —«Descripciones y precios sugeridos por IA» y
 * «Sugerencias de marketing de IA»— cuyas rutas existían en el servidor **y no
 * las llamaba ninguna pantalla**. Se cobraban y no se podían usar.
 *
 * ## La parte que no se podía saltar
 *
 * Antes de darles puerta hubo que arreglar lo que devolvían cuando la IA falla.
 * El precio se replegaba a un `precio × 1,35` con
 * `reasoning: 'Standard competitive pricing strategy'` —una frase que parece un
 * razonamiento y es una constante— **sin ningún campo que lo distinguiera** de
 * una respuesta de verdad. Redactar y marketing devolvían textos enlatados en
 * inglés, ignorando el idioma pedido.
 *
 * Ahora: redactar y marketing **fallan** si la IA no contesta —no hay
 * aritmética que aproxime un texto— y el precio se repliega **marcado**, y esta
 * pantalla lo dice con todas las letras.
 */

/** Un bloque de texto con su botón de copiar. */
function Copiable({ etiqueta, texto }) {
    const { t } = useTranslation();
    const [copiado, setCopiado] = useState(false);

    if (!texto) return null;

    const copiar = async () => {
        try {
            await navigator.clipboard.writeText(texto);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
        } catch {
            // Sin permiso al portapapeles no se avisa con un error: el texto
            // está a la vista y se puede seleccionar a mano.
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-medium text-slate-500">{etiqueta}</span>
                <button
                    type="button"
                    onClick={copiar}
                    className="text-xs text-primary-500 hover:underline flex items-center gap-1"
                >
                    {copiado ? <HiCheck className="w-3 h-3" /> : <HiClipboardCopy className="w-3 h-3" />}
                    {t(copiado ? 'ficha_ia.copiado' : 'ficha_ia.copiar')}
                </button>
            </div>
            <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{texto}</p>
        </div>
    );
}

export default function AyudaDeLaIA({ producto, esPro, onUsarDescripcion }) {
    const { t, i18n } = useTranslation();
    const [pidiendo, setPidiendo] = useState(null);
    const [resultado, setResultado] = useState(null);

    if (!producto) return null;

    const pedir = async (que, llamada) => {
        setPidiendo(que);
        setResultado(null);
        try {
            const res = await llamada();
            setResultado({ que, datos: res.data.data });
        } catch (error) {
            // El motivo llega hasta aquí: `errores.IA_sin_cuota_hoy` y demás
            // están traducidos, así que la pantalla dice si fue la cuota, la
            // clave o un corte, en vez de «algo falló».
            notify.error(mensajeDeError(error, t('errores.IA_ERROR')));
        } finally {
            setPidiendo(null);
        }
    };

    const idioma = i18n.language?.startsWith('es') ? 'es' : 'en';

    const acciones = [
        {
            clave: 'descripcion',
            etiqueta: t('ficha_ia.boton_descripcion'),
            llamada: () => aiAPI.generateDescription(producto.id, 'professional', idioma)
        },
        {
            clave: 'precio',
            etiqueta: t('ficha_ia.boton_precio'),
            llamada: () => aiAPI.getPricing(producto.id, idioma)
        },
        {
            clave: 'marketing',
            etiqueta: t('ficha_ia.boton_marketing'),
            llamada: () => aiAPI.getMarketing(producto.id, idioma)
        }
    ];

    return (
        <div className="border-t border-slate-200 dark:border-slate-700 pt-5 mt-5">
            <h4 className="font-medium text-slate-900 dark:text-white flex items-center gap-2">
                <HiSparkles className="w-4 h-4 text-accent-500" />
                {t('ficha_ia.titulo')}
            </h4>
            <p className="text-xs text-slate-500 mt-1 mb-3">{t('ficha_ia.subtitulo')}</p>

            {/* Sin plan Pro no se pintan botones que van a responder 403: se
                dice de quién es esto y ya. Regla 4. */}
            {!esPro ? (
                <p className="text-sm text-slate-500">{t('ficha_ia.solo_pro')}</p>
            ) : (
                <>
                    <div className="flex flex-wrap gap-2">
                        {acciones.map((a) => (
                            <button
                                key={a.clave}
                                type="button"
                                onClick={() => pedir(a.clave, a.llamada)}
                                disabled={pidiendo !== null}
                                className="btn-secondary btn-sm disabled:opacity-50"
                            >
                                {pidiendo === a.clave ? t('ficha_ia.pensando') : a.etiqueta}
                            </button>
                        ))}
                    </div>

                    {resultado?.que === 'descripcion' && (
                        <div className="mt-4 space-y-3">
                            <Copiable etiqueta={t('imported_products.title_label')} texto={resultado.datos.description?.title} />
                            <Copiable etiqueta={t('ficha_ia.titulo')} texto={resultado.datos.description?.description} />
                            {onUsarDescripcion && resultado.datos.description?.description && (
                                <button
                                    type="button"
                                    onClick={() => onUsarDescripcion(resultado.datos.description)}
                                    className="btn-primary btn-sm"
                                >
                                    {t('ficha_ia.usar_descripcion')}
                                </button>
                            )}
                        </div>
                    )}

                    {resultado?.que === 'precio' && (() => {
                        const p = resultado.datos.pricing;
                        return (
                            <div className="mt-4 space-y-3">
                                {/* Lo primero, y en ámbar: si esto NO lo dijo la
                                    IA hay que saberlo antes de mirar la cifra,
                                    no después. */}
                                {p.source === 'heuristic' && (
                                    <p className="text-xs text-warning-600 dark:text-warning-500 border border-warning-500/40 bg-warning-500/5 rounded-lg p-2">
                                        {t('ficha_ia.estimacion')}
                                    </p>
                                )}

                                <div className="grid grid-cols-3 gap-3 text-center">
                                    {[
                                        ['ficha_ia.precio_minimo', p.minimumPrice, 'text-slate-600 dark:text-slate-300'],
                                        ['ficha_ia.precio_recomendado', p.recommendedPrice, 'text-success-500 text-lg font-semibold'],
                                        ['ficha_ia.precio_maximo', p.maximumPrice, 'text-slate-600 dark:text-slate-300']
                                    ].map(([clave, valor, clase]) => (
                                        <div key={clave}>
                                            <p className="text-xs text-slate-500">{t(clave)}</p>
                                            <p className={clase}>{dinero(valor)}</p>
                                        </div>
                                    ))}
                                </div>

                                {p.reasoning && (
                                    <p className="text-sm text-slate-600 dark:text-slate-300">{p.reasoning}</p>
                                )}
                            </div>
                        );
                    })()}

                    {resultado?.que === 'marketing' && (() => {
                        const m = resultado.datos.marketing;
                        return (
                            <div className="mt-4 space-y-3">
                                <Copiable etiqueta={t('ficha_ia.titular')} texto={m.adCopy?.headline} />
                                <Copiable etiqueta={t('ficha_ia.llamada')} texto={m.adCopy?.callToAction} />
                                {m.hashtags?.length > 0 && (
                                    <Copiable etiqueta={t('ficha_ia.etiquetas')} texto={m.hashtags.join(' ')} />
                                )}
                            </div>
                        );
                    })()}
                </>
            )}
        </div>
    );
}
